import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import PropTypes from 'prop-types';
import _includes from 'lodash/includes';
import _isNil from 'lodash/isNil';
import classnames from 'classnames';
import { withTheme } from '@material-ui/core/styles';
// Core components
import Divider from 'components/core/Divider';
// Models
import mFinance from 'models/cpm/finance';
// Custom
import Tooltip from 'components/core/Tooltip';
import Amount from 'components/finance/Amount';
import AppIcon from 'components/icons/AppIcon';
import {
  cpmPriceRuleDataShape,
  cpmPurchaseOfferShape,
  cpmPurchaseShape,
} from 'constants/PropTypesShapes';

const PriceAmount = withTheme(
  ({
    amount,
    label,
    position,
    priceRule,
    theme,
    tooltip,
  }) => {
    const paddingClasses = {
      'pb-2': position === 'first',
      'pt-2': position === 'last',
      'py-2': !_includes(['first', 'last', 'single'], position),
    };

    return (
      <div className={classnames('d-flex justify-content-between priceRow', paddingClasses)} style={{ gap: '20px' }}>
        <div className="d-flex justify-content-between">
          <LocalizedMessage id={label} />
          {tooltip && (
            <Tooltip content={<LocalizedMessage id={tooltip} />}>
              <AppIcon id="btnInfo" className="pl-2" color={theme && theme.palette.primary.main} />
            </Tooltip>
          )}
        </div>
        <div>
          <Amount amount={amount} priceRule={priceRule} />
        </div>
      </div>
    );
  },
);

PriceAmount.propTypes = {
  amount: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  priceRule: cpmPriceRuleDataShape.isRequired,
  position: PropTypes.oneOf(['first', 'last', 'single']),
  theme: PropTypes.object,
  tooltip: PropTypes.string,
};

PriceAmount.defaultProps = { position: null, theme: null, tooltip: null };

const PurchaseInfoPrice = ({
  data,
  dataType,
  purchases,
  type,
  showDetails,
}) => {
  const isOffer = dataType === 'offer';
  const isPurchase = dataType === 'purchase';

  const {
    discountedAmountInclTax,
    priceRule,
    serviceFeeAmountInclTax,
    totalAmountInclTax,
  } = data;

  const hasServiceFee = !(_isNil(serviceFeeAmountInclTax) || serviceFeeAmountInclTax === 0);
  const isFree = (isOffer && mFinance.offerIsFree(data))
      || (isPurchase && mFinance.purchaseIsFree(data));

  const isPayingWithZeroAmount = !isFree && mFinance.offerAmountToPayIsZero(data);

  const alreadyPaidAmount = mFinance.purchaseSumPaidAmount(purchases);
  const allPurchasesTotalAmount = alreadyPaidAmount + totalAmountInclTax;

  if (isFree || isPayingWithZeroAmount) {
    switch (type) {
      case 'extension':
        return (
          <div className="text-center checkoutCardBody">
            <LocalizedMessage id="info.purchase.freeExtension" />
          </div>
        );
      case 'overstay':
        return (
          <div className="text-center checkoutCardBody">
            <LocalizedMessage id="info.purchase.freeOverstay" />
          </div>
        );
      default:
        return (
          <div className="text-center checkoutCardBody">
            <LocalizedMessage id="info.purchase.free" />
          </div>
        );
    }
  }

  if (hasServiceFee) {
    switch (type) {
      case 'extension':
        return (
          <div className="priceBox">
            {showDetails
              ? (
                <React.Fragment>
                  <PriceAmount
                    amount={discountedAmountInclTax}
                    label="info.purchase.amountToPay"
                    position="first"
                    priceRule={priceRule}
                  />
                  <PriceAmount
                    amount={serviceFeeAmountInclTax}
                    label="info.purchase.serviceFeesAmount"
                    priceRule={priceRule}
                    tooltipId="tooltip.purchase.serviceFeesAmount"
                  />
                  <Divider />
                  <PriceAmount
                    amount={alreadyPaidAmount}
                    label="info.purchase.initialAmount"
                    priceRule={priceRule}
                  />
                  <Divider />
                  <PriceAmount
                    amount={totalAmountInclTax}
                    label="info.purchase.extensionToPay"
                    position="last"
                    priceRule={priceRule}
                  />
                </React.Fragment>
              ) : (
                <PriceAmount
                  amount={totalAmountInclTax}
                  label="info.purchase.amountToPayIncludedServiceFees"
                  priceRule={priceRule}
                  position="single"
                />
              )}
          </div>
        );

      case 'overstay':
        return (
          <div className="priceBox">
            <PriceAmount
              amount={alreadyPaidAmount}
              label="info.purchase.initialAmount"
              position="first"
              priceRule={priceRule}
            />
            <PriceAmount
              amount={discountedAmountInclTax}
              label="info.purchase.amountToPay"
              priceRule={priceRule}
            />
            <PriceAmount
              amount={serviceFeeAmountInclTax}
              label="info.purchase.serviceFeesAmount"
              priceRule={priceRule}
              tooltipId="tooltip.purchase.serviceFeesAmount"
            />
            <Divider />
            <PriceAmount
              amount={totalAmountInclTax}
              label="info.purchase.overstayAmount"
              position="last"
              priceRule={priceRule}
            />
          </div>
        );

      default:
        return (
          <div className="priceBox">
            {showDetails
              ? (
                <React.Fragment>
                  <PriceAmount
                    amount={discountedAmountInclTax}
                    label="info.purchase.amountToPay"
                    position="first"
                    priceRule={priceRule}
                  />
                  <PriceAmount
                    amount={serviceFeeAmountInclTax}
                    label="info.purchase.serviceFeesAmount"
                    priceRule={priceRule}
                    tooltipId="tooltip.purchase.serviceFeesAmount"
                  />
                  <PriceAmount
                    amount={totalAmountInclTax}
                    label="info.purchase.totalAmountToPay"
                    position="last"
                    priceRule={priceRule}
                  />
                </React.Fragment>
              )
              : (
                <PriceAmount
                  amount={totalAmountInclTax}
                  label="info.purchase.amountToPayIncludedServiceFees"
                  priceRule={priceRule}
                  position="single"
                />
              )}
          </div>
        );
    }
  }

  if (!hasServiceFee) {
    switch (type) {
      case 'extension':
        return (
          <div className="priceBox">
            {showDetails ? (
              <React.Fragment>
                <PriceAmount
                  amount={allPurchasesTotalAmount}
                  label="info.purchase.extensionNewAmount"
                  position="first"
                  priceRule={priceRule}
                />
                <PriceAmount
                  amount={alreadyPaidAmount}
                  label="info.purchase.extensionAlreadyPaid"
                  priceRule={priceRule}
                />
                <Divider className="my-2" />
                <PriceAmount
                  amount={totalAmountInclTax}
                  label="info.purchase.extensionToPay"
                  position="last"
                  priceRule={priceRule}
                />
              </React.Fragment>
            ) : (
              <PriceAmount
                amount={totalAmountInclTax}
                label="info.purchase.amountToPay"
                position="single"
                priceRule={priceRule}
              />
            )}
          </div>
        );

      case 'overstay':
        return (
          <div className="priceBox">
            <PriceAmount
              amount={alreadyPaidAmount}
              label="info.purchase.initialAmount"
              position="first"
              priceRule={priceRule}
            />
            <Divider />
            <PriceAmount
              amount={totalAmountInclTax}
              label="info.purchase.overstayAmount"
              position="last"
              priceRule={priceRule}
            />
          </div>
        );

      default:
        return (
          <div className="priceBox">
            <PriceAmount
              amount={totalAmountInclTax}
              label="info.purchase.amountToPay"
              position="single"
              priceRule={priceRule}
            />
          </div>
        );
    }
  }
};

PurchaseInfoPrice.propTypes = {
  data: PropTypes.oneOfType([
    cpmPurchaseOfferShape,
    cpmPurchaseShape,
  ]).isRequired,
  dataType: PropTypes.oneOf(['offer', 'purchase']),
  purchases: PropTypes.arrayOf(cpmPurchaseShape),
  type: PropTypes.oneOf(['initial', 'extension', 'overstay']),
  showDetails: PropTypes.bool,
};

PurchaseInfoPrice.defaultProps = {
  dataType: 'purchase',
  purchases: [],
  type: 'initial',
  showDetails: true,
};

module.exports = PurchaseInfoPrice;



// WEBPACK FOOTER //
// ./components/finance/PurchaseInfoPrice.js